import { ChevronDown, Minus, X, type LucideIcon } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";

import type { ClientWarningDismissal } from "@/api/types/foreignNational";
import { Button } from "@/components/ui/button";
import { FilterChip } from "@/components/ui/filter-chip";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { formatShortDate } from "../header/clientHeaderModel";

export type WarningTone = "red" | "amber" | "emerald" | "neutral";

const BANNER: Record<WarningTone, string> = {
  red: "border-danger-line bg-danger-soft",
  amber: "border-warn-line bg-warn-soft",
  emerald: "border-success-line bg-success-soft",
  neutral: "border-border bg-fill-1"
};

const TITLE: Record<WarningTone, string> = {
  red: "text-danger",
  amber: "text-warn",
  emerald: "text-success",
  neutral: "text-foreground"
};

/** Stored as the reason; the chip shows the Title Case label. */
export const DISMISS_REASONS: Array<{ label: string; value: string }> = [
  { label: "Extension on File", value: "Extension on file" },
  { label: "Change of Status Filed", value: "Change of status filed" },
  { label: "Not Relevant", value: "Not relevant" }
];
export const MAX_REASON_CHARS = 200;

const IconAction: FC<{
  label: string;
  tip: string;
  onClick?: () => void;
  testId?: string;
  children: ReactNode;
}> = ({ label, tip, onClick, testId, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={label}
        onClick={onClick}
        data-testid={testId}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{tip}</TooltipContent>
  </Tooltip>
);

const DismissPopover: FC<{
  testId: string;
  onDismiss: (reason: string | null) => void;
}> = ({ testId, onDismiss }) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [other, setOther] = useState("");
  const reason = (picked ?? other).trim() || null;
  const reset = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setPicked(null);
      setOther("");
    }
  };
  return (
    <Popover open={open} onOpenChange={reset}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Dismiss warning"
              data-testid={`${testId}-dismiss`}
            >
              <X />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Dismiss for Everyone</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" data-testid={`${testId}-dismiss-popover`}>
        <PopoverHeader>
          <PopoverTitle>Dismiss for Everyone?</PopoverTitle>
          <PopoverDescription>
            Hides this warning for the whole firm until the facts behind it
            change. It is recorded in the client&apos;s activity.
          </PopoverDescription>
        </PopoverHeader>
        <div
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label="Reason"
        >
          {DISMISS_REASONS.map((r) => (
            <FilterChip
              key={r.value}
              active={picked === r.value}
              onClick={() => {
                setPicked(picked === r.value ? null : r.value);
                setOther("");
              }}
            >
              {r.label}
            </FilterChip>
          ))}
        </div>
        <Input
          aria-label="Other reason"
          placeholder="Other reason (optional)"
          value={other}
          maxLength={MAX_REASON_CHARS}
          onChange={(e) => {
            setOther(e.target.value);
            setPicked(null);
          }}
        />
        <div className="flex justify-end gap-1.5">
          <Button variant="ghost" size="xs" onClick={() => reset(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="xs"
            onClick={() => {
              onDismiss(reason);
              reset(false);
            }}
          >
            Dismiss
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

type Props = {
  tone: WarningTone;
  icon: LucideIcon;
  title: ReactNode;
  body?: ReactNode;
  /** Small print under the body ("Also lapsed: ..."). */
  footnote?: ReactNode;
  actions?: ReactNode;
  /** The one line shown while minimized ("Status ended 847 days ago"). */
  compactLabel: string;
  minimized: boolean;
  onMinimize: () => void;
  onExpand: () => void;
  /** Omit when the warning can't be dismissed for the firm. */
  onDismiss?: (reason: string | null) => void;
  testId: string;
};

/**
 * A client-profile warning: the full banner, or a one-line strip once the
 * user minimizes it (theirs only), and dismissible for the whole firm with an
 * optional reason.
 */
export const ClientWarning: FC<Props> = ({
  tone,
  icon: Icon,
  title,
  body,
  footnote,
  actions,
  compactLabel,
  minimized,
  onMinimize,
  onExpand,
  onDismiss,
  testId
}) => {
  if (minimized)
    return (
      <div
        role="status"
        className={cn(
          "flex w-fit max-w-full items-center gap-1.5 rounded-md border py-0.5 pr-0.5 pl-2 text-left",
          BANNER[tone]
        )}
        data-testid={`${testId}-minimized`}
      >
        <Icon className={cn("size-3.5 shrink-0", TITLE[tone])} />
        <span className={cn("truncate text-sm font-medium", TITLE[tone])}>
          {compactLabel}
        </span>
        <Button variant="ghost" size="xs" onClick={onExpand}>
          <ChevronDown /> Expand
        </Button>
      </div>
    );
  return (
    <div
      role={tone === "red" ? "alert" : "status"}
      className={cn(
        "flex flex-col gap-3 rounded-md border px-3 py-2.5 text-left sm:flex-row sm:items-start",
        BANNER[tone]
      )}
      data-testid={testId}
    >
      <Icon className={cn("mt-0.5 size-4 shrink-0", TITLE[tone])} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={cn("text-base font-semibold", TITLE[tone])}>
          {title}
        </span>
        {body ? <span className="text-base">{body}</span> : null}
        {footnote ? (
          <span className="text-xs text-muted-foreground">{footnote}</span>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        {actions}
        <TooltipProvider>
          <IconAction
            label="Minimize warning"
            tip="Minimize"
            onClick={onMinimize}
            testId={`${testId}-minimize`}
          >
            <Minus />
          </IconAction>
          {onDismiss ? (
            <DismissPopover testId={testId} onDismiss={onDismiss} />
          ) : null}
        </TooltipProvider>
      </div>
    </div>
  );
};

/** "1 Dismissed Warning · Show": who dismissed each, why, and Restore. */
export const DismissedWarnings: FC<{
  dismissals: ClientWarningDismissal[];
  onRestore: (kind: string) => void;
  testId?: string;
}> = ({ dismissals, onRestore, testId = "dismissed-warnings" }) => {
  if (!dismissals.length) return null;
  const n = dismissals.length;
  return (
    <div
      className="flex items-center gap-1.5 text-sm text-fg-3"
      data-testid={testId}
    >
      {n} Dismissed Warning{n === 1 ? "" : "s"}
      <span aria-hidden>·</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="link" size="xs" className="h-auto px-0 text-sm">
            Show
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80">
          <PopoverHeader>
            <PopoverTitle>Dismissed Warnings</PopoverTitle>
            <PopoverDescription>
              Hidden for the whole firm. They come back on their own when the
              facts change.
            </PopoverDescription>
          </PopoverHeader>
          <ul className="flex flex-col gap-2">
            {dismissals.map((d) => (
              <li
                key={d.kind}
                className="flex items-start gap-2"
                data-testid="dismissed-warning"
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-base font-medium">{d.label}</span>
                  <span className="text-sm text-fg-3">
                    {[
                      d.dismissed_by_name
                        ? `Dismissed by ${d.dismissed_by_name}`
                        : "Dismissed",
                      formatShortDate(d.dismissed_at ?? null)
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  {d.reason ? (
                    <span className="text-sm text-fg-2">{d.reason}</span>
                  ) : null}
                </div>
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() => onRestore(d.kind)}
                >
                  Restore
                </Button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
};
